import { FC, useState } from 'react';

import { Loading } from '@shared/components/Loading';
import { api_ } from '@shared/plugIn/axios';
import { UserRole } from '@shared/types';

import { useAdminTitleHook } from '../hooks/useAdminTitleHook';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyOrder = Record<string, any>;

interface PhoneSearchUser {
  _id: string;
  username: string;
  password?: string;
  info: {
    name: string;
    phoneNumber: string;
  };
  roles: UserRole[];
  bugoBrand?: {
    _id: string;
    type?: string;
    bugoAgency?: {
      corpName?: string;
      nickName?: string;
    };
    bugoFuneralHome?: {
      funeralHomeInfo?:
        | {
            name?: string;
          }
        | string;
    };
  };
  createdAt: string;
  shopOrders: AnyOrder[];
  giftOrders: AnyOrder[];
}

interface PhoneSearchMember {
  _id: string;
  fullName: string;
  phoneNumber: string;
  relationType: string;
  createdAt: string;
  fevent?: {
    deceasedInfo?: {
      name?: string;
      coffinOut?: {
        date?: string | Date | null;
        timeDefined?: boolean;
      };
    };
    funeralHomeInfo?: {
      name?: string;
    };
  };
}

interface PhoneSearchGiftCoupon {
  _id: string;
  code?: string;
  fullCode?: string;
  shareStatus?: string;
  redeemStatus?: string;
  purchasedAt?: string;
  expiresAt?: string;
  recipient?: {
    name?: string;
    phoneNumber?: string;
    requestedAt?: string;
  };
  giftOrder?: {
    _id?: string;
    paymentDetail?: {
      status?: string;
      purchasedAt?: string;
      paymentByCash?: number;
    };
  };
  giftItem?: {
    name?: string;
    giftItemInfo?: {
      name?: string;
    };
  };
  gifticon?: {
    priceRetail?: number;
    deliveryChannel?: string;
    deliveredAt?: string;
  };
}

interface PhoneSearchGiftDelivery {
  _id: string;
  code?: string;
  fullCode?: string;
  deliveryStatus?: string;
  shareStatus?: string;
  quantity?: number;
  purchasedAt?: string;
  expiresAt?: string;
  recipient?: {
    name?: string;
    phoneNumber?: string;
    address?: string;
    submittedAt?: string;
  };
  shipping?: {
    trackingNumber?: string;
    carrier?: string;
    deliveredAt?: string;
  };
  giftOrder?: {
    _id?: string;
    paymentDetail?: {
      status?: string;
      purchasedAt?: string;
      paymentByCash?: number;
    };
  };
  giftItem?: {
    name?: string;
    giftItemInfo?: {
      name?: string;
    };
  };
}

interface PhoneSearchResponse {
  users: PhoneSearchUser[];
  members: PhoneSearchMember[];
  giftCoupons: PhoneSearchGiftCoupon[];
  giftDeliveries: PhoneSearchGiftDelivery[];
}

const roleLabel = (role: UserRole): string => {
  const map: Record<string, string> = {
    [UserRole.Admin]: '관리자',
    [UserRole.BugoAgencyAdmin]: '팀장',
    [UserRole.BugoAgencyBranchAdmin]: '지점장',
    [UserRole.BugoFuneralHome]: '장례식장',
    [UserRole.BugoAgencyWorker]: '직원',
    [UserRole.BugoCustomer]: '고객',
    [UserRole.BugoGuest]: '게스트',
    [UserRole.StoreSeller]: '셀러',
    [UserRole.StoreCustomer]: '스토어고객',
    [UserRole.StoreGuest]: '스토어게스트',
  };
  return map[role] ?? role;
};

const paymentStatusLabel = (status: string): string => {
  const map: Record<string, string> = {
    NotPaid: '미결제',
    Waiting: '입금대기',
    Paid: '결제완료',
    Confirmed: '구매확정',
    CancelRequested: '취소요청',
    Canceled: '취소',
  };
  return map[status] ?? status;
};

const deliveryStatusLabel = (status: string): string => {
  const map: Record<string, string> = {
    BeforePreparing: '준비전',
    Preparing: '준비중',
    Deliverying: '배송중',
    Delivered: '배송완료',
  };
  return map[status] ?? status;
};

const couponRedeemLabel = (status: string): string => {
  const map: Record<string, string> = {
    Available: '교환가능',
    Requested: '교환신청',
    Delivered: '전달완료',
    Rejected: '발송불가',
    Expired: '만료',
    Refunded: '환불',
    Canceled: '취소',
    Disposed: '폐기',
  };
  return map[status] ?? status;
};

const giftDeliveryStatusLabel = (status: string): string => {
  const map: Record<string, string> = {
    WaitingAddress: '배송지대기',
    AddressSubmitted: '배송지입력',
    Preparing: '준비중',
    Shipping: '배송중',
    Delivered: '배송완료',
    Canceled: '취소',
  };
  return map[status] ?? status;
};

const deliveryChannelLabel = (channel: string): string => {
  const map: Record<string, string> = {
    Officecon: '오피스콘',
    GiftiShow: '기프티쇼',
    EumGift: '1566-2373',
  };
  return map[channel] ?? channel;
};

const formatDate = (date: string | Date | undefined): string => {
  if (!date) return '-';
  return new Date(date).toLocaleDateString('ko-KR');
};

const formatDateTime = (date: string | Date | undefined): string => {
  if (!date) return '-';
  const d = new Date(date);
  return `${d.toLocaleDateString('ko-KR')} ${d.getHours().toString().padStart(2, '0')}:${d
    .getMinutes()
    .toString()
    .padStart(2, '0')}`;
};

const formatPrice = (price: number | undefined): string => {
  if (price == null) return '-';
  return price.toLocaleString('ko-KR') + '원';
};

const InfoRow: FC<{ label: string; value: string | undefined }> = ({ label, value }) => (
  <div className="flex justify-between border-b py-2 theme-border-1">
    <span className="text-xs font-bold text-gray-500">{label}</span>
    <span className="text-xs theme-text-1">{value || '-'}</span>
  </div>
);

const InfoRowPair: FC<{
  left: { label: string; value: string | undefined };
  right: { label: string; value: string | undefined };
}> = ({ left, right }) => (
  <div className="grid grid-cols-2 gap-2 border-b py-2 theme-border-1">
    <div className="flex justify-between pr-2">
      <span className="text-xs font-bold text-gray-500">{left.label}</span>
      <span className="text-xs theme-text-1">{left.value || '-'}</span>
    </div>
    <div className="flex justify-between pl-2">
      <span className="text-xs font-bold text-gray-500">{right.label}</span>
      <span className="text-xs theme-text-1">{right.value || '-'}</span>
    </div>
  </div>
);

const getBugoBrandName = (user: PhoneSearchUser): string => {
  if (user.bugoBrand?.bugoAgency) {
    return (
      user.bugoBrand.bugoAgency.corpName || user.bugoBrand.bugoAgency.nickName || '-'
    );
  }
  if (
    user.bugoBrand?.bugoFuneralHome &&
    typeof user.bugoBrand.bugoFuneralHome.funeralHomeInfo === 'object'
  ) {
    return user.bugoBrand.bugoFuneralHome.funeralHomeInfo?.name ?? '-';
  }
  return '-';
};

const ShopOrderCards: FC<{ orders: AnyOrder[] }> = ({ orders }) => (
  <div className="space-y-2">
    {orders.map((order, idx) => (
      <div
        key={order._id ?? idx}
        className="rounded border p-3 text-xs theme-bg-1 theme-border-1"
      >
        <div className="mb-1 font-bold theme-text-1">
          {order.orderDetail?.shopItemEmbed?.name ?? '-'}
        </div>
        <div className="flex flex-wrap gap-x-4 gap-y-1 theme-text-2">
          <span>수량: {order.orderDetail?.quantity ?? '-'}</span>
          <span>{formatPrice(order.paymentDetail?.paymentByCash)}</span>
          <span>{paymentStatusLabel(order.paymentDetail?.status)}</span>
          <span>{deliveryStatusLabel(order.deliveryDetail?.status)}</span>
          <span>{formatDate(order.paymentDetail?.purchasedAt)}</span>
        </div>
      </div>
    ))}
  </div>
);

const GiftOrderCards: FC<{ orders: AnyOrder[] }> = ({ orders }) => (
  <div className="space-y-2">
    {orders.map((order, idx) => {
      const items: AnyOrder[] = order.orderDetails ?? [];
      const itemSummary =
        items.length > 0
          ? items.map((it: AnyOrder) => it.giftItemEmbed?.name).join(', ')
          : '-';
      const totalQty = items.reduce(
        (sum: number, it: AnyOrder) => sum + (it.quantity ?? 0),
        0,
      );
      return (
        <div
          key={order._id ?? idx}
          className="rounded border p-3 text-xs theme-bg-1 theme-border-1"
        >
          <div className="mb-1 font-bold theme-text-1">{itemSummary}</div>
          <div className="flex flex-wrap gap-x-4 gap-y-1 theme-text-2">
            <span>수량: {totalQty || '-'}</span>
            <span>{formatPrice(order.paymentDetail?.paymentByCash)}</span>
            <span>{paymentStatusLabel(order.paymentDetail?.status)}</span>
            <span>{formatDate(order.paymentDetail?.purchasedAt)}</span>
          </div>
        </div>
      );
    })}
  </div>
);

const UserCard: FC<{ user: PhoneSearchUser }> = ({ user }) => {
  const [orderOpen, setOrderOpen] = useState(false);
  const totalOrders = (user.shopOrders?.length ?? 0) + (user.giftOrders?.length ?? 0);

  return (
    <div className="rounded-lg border p-4 theme-bg-1 theme-border-1">
      <div className="mb-2 flex items-center justify-between">
        <span className="text-sm font-bold theme-text-1">{user.info?.name || '-'}</span>
        <span className="rounded-full bg-blue-100 px-2 py-0.5 text-[10px] font-bold text-blue-700">
          {user.roles.map(roleLabel).join(', ')}
        </span>
      </div>
      <div className="space-y-0">
        <InfoRowPair
          left={{ label: 'ID', value: user.username }}
          right={{ label: 'PW', value: user.password }}
        />
        <InfoRowPair
          left={{ label: '전화번호', value: user.info?.phoneNumber }}
          right={{ label: '소속', value: getBugoBrandName(user) }}
        />
      </div>
      {totalOrders > 0 && (
        <div className="mt-3">
          <button
            type="button"
            onClick={() => setOrderOpen(!orderOpen)}
            className="w-full rounded bg-gray-100 py-2 text-xs font-bold text-blue-600"
          >
            {orderOpen ? '구매기록 닫기' : `구매기록 보기 (${totalOrders}건)`}
          </button>
          {orderOpen && (
            <div className="mt-3 space-y-3">
              {user.shopOrders?.length > 0 && (
                <div>
                  <p className="mb-1.5 text-xs font-bold theme-text-1">
                    부고 주문 ({user.shopOrders.length}건)
                  </p>
                  <ShopOrderCards orders={user.shopOrders} />
                </div>
              )}
              {user.giftOrders?.length > 0 && (
                <div>
                  <p className="mb-1.5 text-xs font-bold theme-text-1">
                    답례 주문 ({user.giftOrders.length}건)
                  </p>
                  <GiftOrderCards orders={user.giftOrders} />
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const GiftCouponCard: FC<{ coupon: PhoneSearchGiftCoupon }> = ({ coupon }) => {
  const itemName = coupon.giftItem?.giftItemInfo?.name || coupon.giftItem?.name || '-';
  return (
    <div className="rounded-lg border p-4 theme-bg-1 theme-border-1">
      <div className="mb-2 flex items-center justify-between">
        <span className="text-sm font-bold theme-text-1">{itemName}</span>
        <span className="rounded-full bg-purple-100 px-2 py-0.5 text-[10px] font-bold text-purple-700">
          {couponRedeemLabel(coupon.redeemStatus ?? '')}
        </span>
      </div>
      <div className="space-y-0">
        <InfoRowPair
          left={{ label: '코드', value: coupon.fullCode || coupon.code }}
          right={{ label: '수신자', value: coupon.recipient?.name }}
        />
        <InfoRowPair
          left={{ label: '수신번호', value: coupon.recipient?.phoneNumber }}
          right={{
            label: '결제',
            value: paymentStatusLabel(coupon.giftOrder?.paymentDetail?.status ?? ''),
          }}
        />
        <InfoRowPair
          left={{
            label: '결제금액',
            value: formatPrice(coupon.giftOrder?.paymentDetail?.paymentByCash),
          }}
          right={{
            label: '구매일',
            value: formatDate(
              coupon.giftOrder?.paymentDetail?.purchasedAt ?? coupon.purchasedAt,
            ),
          }}
        />
        <InfoRowPair
          left={{
            label: '신청일',
            value: formatDateTime(coupon.recipient?.requestedAt),
          }}
          right={{
            label: '발송일',
            value: formatDateTime(coupon.gifticon?.deliveredAt),
          }}
        />
        {coupon.gifticon && (
          <InfoRowPair
            left={{
              label: '기프티콘가',
              value: formatPrice(coupon.gifticon.priceRetail),
            }}
            right={{
              label: '발송매체',
              value: coupon.gifticon.deliveryChannel
                ? deliveryChannelLabel(coupon.gifticon.deliveryChannel)
                : '-',
            }}
          />
        )}
      </div>
    </div>
  );
};

const GiftDeliveryCard: FC<{ delivery: PhoneSearchGiftDelivery }> = ({ delivery }) => {
  const itemName =
    delivery.giftItem?.giftItemInfo?.name || delivery.giftItem?.name || '-';
  return (
    <div className="rounded-lg border p-4 theme-bg-1 theme-border-1">
      <div className="mb-2 flex items-center justify-between">
        <span className="text-sm font-bold theme-text-1">{itemName}</span>
        <span className="rounded-full bg-orange-100 px-2 py-0.5 text-[10px] font-bold text-orange-700">
          {giftDeliveryStatusLabel(delivery.deliveryStatus ?? '')}
        </span>
      </div>
      <div className="space-y-0">
        <InfoRowPair
          left={{ label: '코드', value: delivery.fullCode || delivery.code }}
          right={{
            label: '수량',
            value: delivery.quantity ? String(delivery.quantity) : '-',
          }}
        />
        <InfoRowPair
          left={{ label: '수신자', value: delivery.recipient?.name }}
          right={{ label: '수신번호', value: delivery.recipient?.phoneNumber }}
        />
        <InfoRowPair
          left={{
            label: '결제',
            value: paymentStatusLabel(delivery.giftOrder?.paymentDetail?.status ?? ''),
          }}
          right={{
            label: '구매일',
            value: formatDate(
              delivery.giftOrder?.paymentDetail?.purchasedAt ?? delivery.purchasedAt,
            ),
          }}
        />
        {delivery.shipping?.trackingNumber && (
          <InfoRowPair
            left={{ label: '운송장', value: delivery.shipping.trackingNumber }}
            right={{ label: '택배사', value: delivery.shipping.carrier }}
          />
        )}
      </div>
    </div>
  );
};

const formatCoffinOut = (
  coffinOut: { date?: string | Date | null; timeDefined?: boolean } | undefined,
): string => {
  if (!coffinOut?.date) return '-';
  const d = new Date(coffinOut.date);
  const dateStr = d.toLocaleDateString('ko-KR');
  if (!coffinOut.timeDefined) return dateStr;
  const time = `${d.getHours().toString().padStart(2, '0')}:${d
    .getMinutes()
    .toString()
    .padStart(2, '0')}`;
  return `${dateStr} ${time}`;
};

const MemberCard: FC<{ member: PhoneSearchMember }> = ({ member }) => (
  <div className="rounded-lg border p-4 theme-bg-1 theme-border-1">
    <div className="mb-2 flex items-center justify-between">
      <span className="text-sm font-bold theme-text-1">{member.fullName}</span>
      <span className="rounded-full bg-green-100 px-2 py-0.5 text-[10px] font-bold text-green-700">
        {member.relationType || '상주'}
      </span>
    </div>
    <div className="space-y-0">
      <InfoRow label="상주번호" value={member.phoneNumber} />
      <InfoRowPair
        left={{ label: '고인명', value: member.fevent?.deceasedInfo?.name }}
        right={{ label: '장례식장', value: member.fevent?.funeralHomeInfo?.name }}
      />
      <InfoRow
        label="발인"
        value={formatCoffinOut(member.fevent?.deceasedInfo?.coffinOut)}
      />
    </div>
  </div>
);

const AdminPhoneSearchPage: FC = () => {
  useAdminTitleHook('전화번호 검색');

  const [phoneInput, setPhoneInput] = useState('');
  const [result, setResult] = useState<PhoneSearchResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  const onSearch = async () => {
    const trimmed = phoneInput.trim();
    if (!trimmed) return;

    setLoading(true);
    setSearched(true);
    try {
      const { data } = await api_.get<PhoneSearchResponse>(
        `/admin/phone-search?phoneNumber=${encodeURIComponent(trimmed)}`,
      );
      setResult(data);
    } catch {
      setResult({ users: [], members: [], giftCoupons: [], giftDeliveries: [] });
    } finally {
      setLoading(false);
    }
  };

  console.log(result);

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') onSearch();
  };

  const hasUsers = result && result.users.length > 0;
  const hasMembers = result && result.members.length > 0;
  const hasCoupons = result && result.giftCoupons?.length > 0;
  const hasDeliveries = result && result.giftDeliveries?.length > 0;
  const noResults =
    searched &&
    !loading &&
    result &&
    !hasUsers &&
    !hasMembers &&
    !hasCoupons &&
    !hasDeliveries;

  return (
    <div className="space-y-4 p-4">
      <div className="flex items-center space-x-2">
        <input
          type="tel"
          inputMode="tel"
          placeholder="전화번호를 입력하세요"
          className="w-full rounded-lg border px-4 py-3 text-sm font-bold theme-text-1 theme-bg-1 theme-border-1 placeholder:theme-text-6"
          value={phoneInput}
          onChange={(e) => setPhoneInput(e.target.value)}
          onKeyDown={onKeyDown}
        />
        <button
          type="button"
          onClick={onSearch}
          className="shrink-0 rounded-lg bg-blue-500 px-5 py-3 text-sm font-bold text-white active:bg-blue-600"
        >
          검색
        </button>
      </div>

      {loading && (
        <div className="center-box h-40">
          <Loading />
        </div>
      )}

      {noResults && (
        <div className="center-box h-40">
          <p className="text-sm theme-text-6">검색 결과가 없습니다.</p>
        </div>
      )}

      {!loading && hasUsers && (
        <div className="space-y-3">
          <p className="text-sm font-bold theme-text-1">
            유저 검색결과: {result.users.length}건
          </p>
          {result.users.map((user) => (
            <UserCard key={user._id} user={user} />
          ))}
        </div>
      )}

      {!loading && hasMembers && (
        <div className="space-y-3">
          <p className="text-sm font-bold theme-text-1">
            상주 검색결과: {result.members.length}건
          </p>
          {result.members.map((member) => (
            <MemberCard key={member._id} member={member} />
          ))}
        </div>
      )}

      {!loading && hasCoupons && (
        <div>
          <p className="mb-2 text-sm font-bold theme-text-1">
            답례 쿠폰 검색결과: {result.giftCoupons.length}건
          </p>
          <div className="grid grid-cols-2 gap-3">
            {result.giftCoupons.map((coupon) => (
              <GiftCouponCard key={coupon._id} coupon={coupon} />
            ))}
          </div>
        </div>
      )}

      {!loading && hasDeliveries && (
        <div>
          <p className="mb-2 text-sm font-bold theme-text-1">
            답례 배송 검색결과: {result.giftDeliveries.length}건
          </p>
          <div className="grid grid-cols-2 gap-3">
            {result.giftDeliveries.map((delivery) => (
              <GiftDeliveryCard key={delivery._id} delivery={delivery} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPhoneSearchPage;
